// The page about one device (#292): its front drawn as a faceplate, first,
// and then what it is. It appears when the address names exactly one device
// (#254), so the way to it is the way to every object — a name that is a
// link (#263) — and there is no second route to keep in step.
//
// What the faceplate is drawn from is what `/v1` exposes: the port intent a
// site holds for a switch and the uplink it reports, from
// `/switches/{serial}/ports` (§11.3); and, when the device resolves to a
// catalogue model, that model's full port set, its authored layout and its
// photo, from `/device-models/{model}` (ADR-0036, #290, #291, #310, #298).
// Which model that is comes from `GET /devices/{serial}` — the one read that
// carries `catalogue_model` fresh, decided by the model string the device
// declares for itself, else by the ports it declares, and never from its type
// string (ADR-0193) — so this page reads the device once more than
// the list did, and pays for nothing per device on the list. The same read
// carries the device's own address (#154), which lives in its state report
// and costs the same per-device call, so it is here and not on the list. A device that
// resolves to no model, or to more than one, says so in words and draws its
// intent alone. Live state arrives with #295. An access point's faceplate is
// its catalogue model's ports (ADR-0036) with the links declared at it, and
// nothing else is painted on it. What they are as wired ports was decided by
// ADR-0052 (#264, ruled 2026-09-23) and where an access point's intent goes by
// ADR-0239; describing it is Design > Wired's, not this page's. **Its Ports
// tab reads them** (#747, #1353): the ports its own state report names, with
// their link, beside the intent Design holds — through `WiredPortRow`, the row
// Operate › Wired's table renders them with, so the two cannot disagree.
//
// **An access point's plate is a thumbnail, and a click opens it whole** (#1889, Gabriel
// Provencher's review of the 2026-10-20 mockup board, concept 2: *"Device page AP image is
// oversize"*). About 104 px of the model's photo stands in for the plate, which `FACE_TALL` held
// to 420 px, as the identity card's leading column and inside its frame (#1894: *"image outside
// the frame"*); pressing it opens the plate as it was drawn, its legend and its caption, in a
// dialog. The thumbnail and its dialog are `PlateThumb.tsx`'s, shared with the catalogue's
// access-point cards since #2001. **A switch keeps its faceplate**, because there the plate is
// the port selector every tab reads.
//
// **The faceplate is drawn once, at the head, above the tabs** (#589). It is
// computed here from the reads this page makes — the intent, the live state,
// the links, the model, and the plan the Ports tab asks for — and handed to
// `DeviceFaceHead` and to the Ports tab, which used to draw its own, as did
// the Links tab. The set of ports pressed on it is this page's state, read by
// every tab: the Ports tab's detail and editors open for it, the Links tab
// declares from it. The plan is this page's for the same reason: the change
// layer it paints is on the face whichever tab is open.
//
// **The sections are tabs** (#388): the identity once, at the top, then one
// of Ports · Links · Neighbourhood · Console · Audit under a tab strip, the
// open one named by the `tab` facet in the address so a link can open a
// device on its console or its audit and the back button returns to the
// section left. The reads are not moved by the tabs: the port intent, the
// live state, the links and the site's devices are read here once, because
// the faceplate's cabled layer and the neighbourhood both need the links;
// only the console mounts and the audit reads per tab.
//
// **What the live read says of itself is said where the read is drawn**
// (#514): under the face at the head, on every tab (#589) — reading, refused,
// unanswered, or answered and when the switch wrote the report — with its
// `Read again`, which reloads this page's one read for the face, the port
// detail and the Neighbourhood alike. When the answer was read is the page's
// fact too (`useReportTimes`), kept here so a tab mounting later shows the
// read's time and not its own.
//
// For a switch the ports are read here (#293): the table and the port detail
// live in `Ports.tsx`, and the port pressed on the faceplate is the row
// highlighted in the table — one selection, one face. The held and running
// pair, the console read and the provider's part of what the controller holds
// are one Configuration section under it (`Configuration.tsx`, #1801), for both
// families, so the page says the pair once. **They are
// managed in Design** (#875, ADR-0149 decision 1): the editor, the plan and
// the apply are Design › Wired's Ports tab, where the same component is
// handed its writes, and this page hands it none — its *Plan these ports in
// Design* lands on the same switch and the port pressed here (the page's one
// *Change this in Design* is the head's, since #1801). The Links and
// Neighbourhood tabs read the same way; a link is declared, re-worded and
// withdrawn on Design › Wired's topology.
//
// The site's declared links are read here too (#297, `/sites/{id}/links`),
// once for the page: the ones that end at this device are painted on the
// faceplate and listed in the Links section (`Links.tsx`), where the pressed
// port is also where a new link starts from.
//
// Two more reads are the page's and are handed down rather than made twice
// (#371, #198): a switch's live port state (`useLiveState`), drawn on the
// faceplate, in the port detail and in the neighbourhood; and the site's
// device list (`useSiteDevices`), for the names a link's far end carries
// only as a serial, for the link picker, and for the switches an access
// point's neighbourhood asks about. The list is read when something needs
// it — an access point's page always, a switch's once a link touches it or
// one is being declared — through the per-cell cache the list pays (§9.5).
import { api } from "../../api/client";
import { SKIP, useQuery } from "../../api/hooks";
import { plural } from "../../dashboard/format";
import { readDevice, useAddressedDevice } from "../../shell/device";
import { useFacets } from "../../shell/facets";
import { AGE_TICK_MS, ageWords, useNow } from "../../shell/freshness";
import { ScopeLink, siteScopeQuery, useScope } from "../../shell/scope";
import { EntityRef, IdRef } from "../../shell/EntityRef";
import type { Entity } from "../../shell/entity";
import { Card, Info, Loading, ProblemBox, TabPanel, Tabs, When, type Tab } from "../../ui";
import { lazy, Suspense, useState } from "react";
import { AuditList } from "../AuditList";
import type { components } from "../../api/schema";
import type { useCarried } from "../../carried";
import { absentExplain, isAbsent, notLiveExplain, type Device, type FirmwareDelivery } from "./commands";
import FirmwareFact from "./FirmwareFact";
import { DeviceFaceHead, type Face } from "./DeviceHead";
import { ClientsCell, ConfigurationCell, type useClients, type useConvergence } from "./DevicesTable";
import DeviceLinks, { useLinks, useSiteDevices } from "./Links";
import Configuration from "./Configuration";
import { SwitchHealth } from "./SwitchHealth";
import AccessPointReach, { SwitchUplinkReach } from "./Reach";
import RadioOverrides from "./RadioOverrides";
import SwitchPorts, { SwitchFaceCaption, facePorts, photoOf, portsOf, useReportTimes, type Plan } from "./Ports";
import { SwitchVlanSummary } from "./switchVlans";
import { documentKeyedRead } from "./keyedBy";
import { useTagSpace } from "./PortsEditor";
import { cabledOf, otherEnd } from "./verdicts";
import { can } from "../../authz";
import { useSession } from "../../shell/session";
import RootPasswordReadings from "./RootPassword";
import { AccessPointPortsPanel } from "../wired/WiredPortRow";
import ApLedsFact from "./ApLedsFact";
import { DeviceThumb } from "./PlateThumb";
import ModelSheet, { hasSheet } from "./ModelSheet";
import SsidCeilingFact from "./SsidCeiling";
import DeviceDiagnosis from "./DeviceDiagnosis";
import { RadiusVlanFact, RadiusVlanNotice } from "./RadiusVlanPath";

/** The device, for the menu its identifiers open (#2367). */
const entityOf = (d: Device): Entity => ({ kind: "device", serial: d.serial, name: d.name || undefined, mac: d.mac, switch: d.category === "switch", siteId: d.site_id, siteName: d.site_name });

export type DeviceModel = components["schemas"]["DeviceModel"];
type ModelMatch = components["schemas"]["DeviceModelMatch"];

/** The path the API serves a model's photo at, with the digest the catalogue
 * names so the browser may keep it for good (ADR-0003). */
export function modelImageURL(model: string, digest: string): string {
  const path = "/v1/device-models/{model}/image/{digest}".replace("{model}", encodeURIComponent(model)).replace("{digest}", digest);
  return (typeof window !== "undefined" ? window.location.origin : "") + path;
}

/** The device's own read, which is the one that carries `catalogue_model`
 * and, for an online device, `lan_address` — the two facts that cost a read
 * per device and that the list therefore omits (§9.5, #154), the address
 * unless the list's caller asks for it (ADR-0391). No serial, no
 * read: the page calls it once, before it knows whether the address names a
 * device, and shares the answer between the head and the detail (#329).
 *
 * The device the address names is read by the shell (`shell/device.tsx`,
 * #1311), whose context bar needs the same answer, so this holds that one
 * read rather than making a second; any other serial — a link's far end — is
 * read here. `reload` reads it again, and is the page's to call after an act
 * on the device or an edit to it. */
export function useDeviceModel(serial: string | undefined) {
  const shared = useAddressedDevice(serial);
  const own = useQuery(() => serial && !shared ? readDevice(serial) : SKIP, [serial, !shared]);
  const one = shared ?? own;
  const match = one.data?.catalogue_model;
  const recognised = match?.state === "recognised" ? match.model : undefined;
  const model = useQuery(() => recognised
    ? api.GET("/device-models/{model}", { params: { path: { model: recognised } } })
    : SKIP, [recognised]);
  return { match, matchStatus: one.status, model: model.data, modelStatus: model.status, read: one.data, reload: one.reload };
}

/** The address the device holds on its own network, in words when there is
 * none: a device the cell holds no record of is not asked and is not called
 * offline (ADR-0219), and an online one whose report carries no address is
 * said to have reported none rather than shown a dash that reads as "not
 * loaded". An offline device's is its last reported one, in the past tense
 * (ADR-0301). */
function AddressWords({ device, read, status }: { device: Device; read?: Device; status: string }) {
  if (device.online_state !== "offline" && read?.lan_address) return <code>{read.lan_address}</code>;
  if (status === "loading") return <span className="muted">…</span>;
  if (status !== "ready") return <span className="muted">—</span>;
  // Restates ADR-0219 decision 5 and decision 3 — a device the cell's gateway holds no record
  // of is its own state and not offline, so it is not asked and is not called offline.
  // ADR-0301 decision 7a keeps it so: nothing past is shown for it either.
  if (device.online_state === "unknown") return <span className="muted">the controller holds no record; not asked</span>;
  if (device.online_state === "offline") {
    // Restates ADR-0301 decision 8 (`8a`) and decisions 1b and 5a — an offline device's address is
    // the one its newest report the cell still holds presented it at: past tense, the value and its
    // age, the exact time on hover, and never in the present tense. Its depth is the cell's, so the
    // words for none say the cell no longer holds a report rather than that the device has none.
    return read?.last_lan_address
      ? <>last reported <PastValue v={read.last_lan_address} /></>
      : <span className="muted">offline; its controller no longer holds a state report</span>;
  }
  return <span className="muted">not reported</span>;
}

/** Where the device's connection came from, as the gateway saw it. For an
 * offline device it is past: the copy's last kept value, or during the
 * gateway's stale-session hold the session's own, never in the present tense
 * (ADR-0301 decision 8). */
function SeenFromWords({ device, read }: { device: Device; read?: Device }) {
  if (device.online_state !== "offline") {
    return device.ip_address ? <>{device.ip_address}</> : <span className="muted">—</span>;
  }
  // Restates ADR-0301 decision 8 (`8a`) and decisions 2b and 5c — the last address the connection
  // came from while it was connected, kept by Zenith after the gateway blanked it, with the time
  // Zenith last saw it; past tense even while the gateway still holds a stale session (ADR-0097).
  if (read?.last_ip_address) return <>last seen from <PastValue v={read.last_ip_address} /></>;
  if (device.ip_address) {
    return <>last seen from <code>{device.ip_address}</code>{device.last_contact_at && <> · as of its last contact, <When value={device.last_contact_at} /></>}</>;
  }
  return <span className="muted">offline; the controller keeps no address once the session ends</span>;
}

/** A value that is no longer current: the value, its age in words, and the
 * exact time on hover (ADR-0301 decisions 3a and 8a). */
function PastValue({ v }: { v: { address: string; observed_at: string } }) {
  const at = new Date(v.observed_at);
  const now = useNow(AGE_TICK_MS);
  return (
    <>
      <code>{v.address}</code> ·{" "}
      <time dateTime={v.observed_at} title={at.toISOString()}>{ageWords(now - at.getTime())} ago</time>
    </>
  );
}

/** A catalogue model's name, as a link to its card on the catalogue page
 * (#354): the model a device resolved to, or one of the candidates it did not
 * resolve between. The hash carries the model first and then the other
 * candidates (#375), so the page rings every one the device's ports match
 * and the card says what it could not tell this one from — the operator is
 * not left holding the other name. */
function ModelLink({ model, others = [] }: { model: string; others?: string[] }) {
  return <ScopeLink to="/device-models" hash={[model, ...others].join(",")} title="Open this model in the catalog"><code>{model}</code></ScopeLink>;
}

/** The model fact in words: the model, or why there is none.
 *
 * Restates ADR-0193 decisions 3a and 4a: where the string the device declares
 * wins over ports that match another model, the ports' model is said beside
 * it, and where the ports decided because no model records the string, the
 * string is named. Neither is resolved out of sight.
 *
 * Restates ADR-0307 decision 4a: where the string was the one the device's
 * capabilities record declared, because the cell's record holds none, the line
 * says so — the Manufacturer fact stays empty, and this is why the model is
 * known anyway. */
export function ModelWords({ match }: { match: ModelMatch }) {
  if (match.state === "recognised" && match.model) {
    return (
      <span>
        <ModelLink model={match.model} />
        {match.ports_model && <span className="dim"> · its ports match <ModelLink model={match.ports_model} /></span>}
        {match.unrecorded_string && <span className="dim"> · it declares <code>{match.unrecorded_string}</code>, which no catalog model records yet</span>}
        {match.declared_from === "capabilities" && <span className="dim"> · declared by the device's capabilities record; the controller's record holds no model string</span>}
      </span>
    );
  }
  const candidates = match.candidates ?? [];
  return (
    <span>
      <span>Not recognized</span>
      {candidates.length > 0 && <> · one of {candidates.map((c, i) => <span key={c}>{i > 0 && ", "}<ModelLink model={c} others={candidates.filter((o) => o !== c)} /></span>)}</>}
      {/* The server's reason is why, not what (UX-012): the line says the state
          and its candidates, and the reason sits behind the ⓘ beside it. */}
      {match.reason && <Info label="Why the model is not recognized">{match.reason}</Info>}
    </span>
  );
}

/** A switch's port intent and its reported uplink, read for the one switch
 * on screen and for nothing else: an access point asks for none. */
export function usePorts(serial: string | undefined) {
  return useQuery(() => serial
    ? api.GET("/switches/{serial}/ports", { params: { path: { serial } } })
    : SKIP, [serial]);
}

/** A switch's stored port intent, which asks no cell (#2238, ADR-0531): the
 * rows the Ports tab draws before the whole read, which waits on the cell's
 * device listing, has answered. */
export function useStoredPorts(serial: string | undefined) {
  return useQuery(() => serial
    ? api.GET("/switches/{serial}/ports/intent", { params: { path: { serial } } })
    : SKIP, [serial]);
}

/** A switch's live port state (§11.3, ADR-0045 decision 3): one read when
 * the page opens and one more each time the operator asks, stored nowhere
 * and never polled. The page's one read of it (#371), shared by the ports
 * section, the port detail and the neighbourhood. */
export function useLiveState(serial: string | undefined) {
  return useQuery(() => serial
    ? api.GET("/switches/{serial}/ports/state", { params: { path: { serial } } })
    : SKIP, [serial]);
}

// The neighbourhood draws with the topology's pieces, which live in their
// own chunk (#201): loaded when a device's page renders it, not before.
const Neighbourhood = lazy(() => import("./Neighbourhood"));

// Provider staff only and one tab of many, so it is not in the bundle every
// operator loads (#201's reasoning, applied to a surface most callers can
// never open).
const Diagnostics = lazy(() => import("./Diagnostics"));

function uptime(seconds?: number): string | undefined {
  if (!seconds || seconds <= 0) return undefined;
  const d = Math.floor(seconds / 86400), h = Math.floor((seconds % 86400) / 3600), m = Math.floor((seconds % 3600) / 60);
  return d > 0 ? `${d} ${plural(d, "day")} ${h} h` : h > 0 ? `${h} h ${m} min` : `${m} min`;
}

/** The sections of a device's page, in the order the strip shows them; the
 * `tab` facet carries one of these ids and anything else reads as `ports`. */
export const TABS = ["ports", "links", "neighbourhood", "console", "audit", "diagnostics"] as const;
export type DeviceTab = (typeof TABS)[number];
export function tabOf(v: string | undefined): DeviceTab {
  return (TABS as readonly string[]).includes(v ?? "") ? (v as DeviceTab) : "ports";
}

export default function DeviceDetail({ device, name: shownName, siteId, mayConfigure, mayMaintain = false, mayConsole, mayReadRunning = false, mayAudit, mayDiagnose, mayAskAssistant = false, one, carried, clients, convergence, onConsole, firmware }: {
  device: Device;
  /** The name every page shows for the device (#1907, `pieces.tsx`): the bound planned device's
   * name where there is one. The device's own name where it is left out. */
  name?: string;
  /** The site in scope, whose links this page reads. */
  siteId: string;
  /** `network.write` at the device's site: whether the switch's ports and its
   * links may be changed in Design, which the page then offers the way to. */
  mayConfigure: boolean;
  /** `device.write` or `planner.write` at the site: whether the device's own
   * record may be changed in Design › Physical › Equipment (#875). */
  mayMaintain?: boolean;
  /** The console capability for this device's class at the site (`device.console.switch`
   * or `device.console.ap`, ADR-0155): whether the Console tab can open one. */
  mayConsole: boolean;
  /** `device.cli.read` at the site: whether a switch's running configuration can be read (ADR-0115). */
  mayReadRunning?: boolean;
  /** `audit.read` at the site: whether the Audit tab exists at all (#388). */
  mayAudit: boolean;
  /** The superuser flag, not a capability: `device.diagnostics.read` belongs
   * to no role, and the read answers 404 to everyone below the provider
   * (ADR-0092 decision 4). The tab is **hidden** rather than shown and
   * refused, for the reason §6.3 gives — a tab that appears and then 404s
   * tells a customer that a surface exists which is not theirs. */
  mayDiagnose: boolean;
  /** `ai.use` at the device's site (ADR-0210 decision 5a): whether the
   * Assistant panel, which asks for this device's diagnosis, is drawn (#1864).
   * Not `mayDiagnose`, which is the provider's window and not the model's input. */
  mayAskAssistant?: boolean;
  /** Opens this device's console, in a window of its own (#537): the head's act, offered from the tab as well. */
  onConsole(d: Device): void;
  /** The device's own read, made once by the page and shared with its head (#329). */
  one: ReturnType<typeof useDeviceModel>;
  /** The site's carried state and client counts, the page's own reads: the
   * two facts the table's row carried and the detail now carries instead,
   * because the row is not drawn under a page that is about one device. */
  carried: ReturnType<typeof useCarried>;
  clients: ReturnType<typeof useClients>;
  /** The site's convergence read, the page's own: an access point's generation pair and state (#369). */
  convergence?: ReturnType<typeof useConvergence>;
  /** An access point's Firmware fact as a way into the page's Firmware card (#1889): whether the
   * caller may command it, the page's busy lane, the act's opener and the page's delivery read.
   * Absent for a switch, whose firmware arpIQ does not upgrade (ADR-0013). */
  firmware?: { mayUpgrade: boolean; busy: boolean; onUpgrade(): void; deliveries?: FirmwareDelivery[] | "unread" };
}) {
  const name = shownName ?? device.name;
  const isSwitch = device.category === "switch";
  const absent = isAbsent(device);
  // **Nothing is asked of a device the cell does not have.** The port read
  // reaches the switch for its uplink and refuses with 409 (ADR-0040 decision
  // 5), so asking would replace the explanation below with a controller
  // refusal — the same fact, said worse, after a round trip.
  const ports = usePorts(isSwitch && !absent ? device.serial : undefined);
  const live = useLiveState(isSwitch && !absent ? device.serial : undefined);
  const times = useReportTimes(live.status === "ready" ? live.data : undefined);
  const { matchStatus, model, read } = one;
  // The set the face at the head gathers (#467, #473), the page's (#589):
  // the single selection the tabs read is the set when it holds one port.
  const [picked, setPicked] = useState<string[]>([]);
  const selected = picked.length === 1 ? picked[0] : undefined;
  // The plan on screen, the page's (#589): made and consumed on the Ports
  // tab, painted on the face at the head; when it was read is what the
  // editor's held values are dated by (#571).
  const [planned, setPlanned] = useState<{ plan: Plan; readAt: Date }>();
  const onPlan = (plan: Plan | undefined) => setPlanned(plan ? { plan, readAt: new Date() } : undefined);
  const up = uptime(device.uptime_seconds);
  const links = useLinks(siteId);
  const touched = (links.data?.links ?? []).some((l) => otherEnd(l, device.serial));
  // Which section is open (#388): the address's, so a link and the back
  // button carry it; nothing is remembered.
  const { facets, set } = useFacets();
  const wanted = tabOf(facets.tab);
  // An access point's page asks for the site's switches alone — the list's
  // own `category` filter (§17.2) — since they are what observes it and what
  // a link to it names; a switch's page needs every device, for the names of
  // the access points its links end at and for the way up to the WAN on the
  // Neighbourhood tab (#586), where the switch of the site on each hop's
  // uplink is looked up in it and a link is declared (#588).
  const devices = useSiteDevices(!isSwitch || touched || wanted === "neighbourhood" ? siteId : undefined, isSwitch ? undefined : "switch");
  // The other end named by serial: this page has one device, not the site's
  // list, and the list is read only once there is a link to name.
  const cabled = cabledOf(links.data?.links ?? [], device.serial, (s) => s);
  // The face, once (#589): a switch's from its intent, its uplink, the plan,
  // the live read and the links (`portsOf`); an access point's from its
  // catalogue model with the links declared at it — its intent `unread`,
  // since the face paints none of an access point's intent (its Ports tab
  // reads it), rather than "no intent". None for a switch whose intent has not been read, or an access
  // point of no recognised model.
  // The face, the Links tab and the neighbourhood name a port by a
  // configuration's word, so they take the read through `documentKeyed`
  // (ADR-0266): a switch answered from its report alone draws nothing live on
  // its face and judges no link. The Ports tab and the read's own line take
  // the answer as it came, and the tab lists the report's ports as the report's.
  const judged = documentKeyedRead(live);
  const liveSet = isSwitch && judged.status === "ready" ? judged.data : undefined;
  // The site's tag space (ADR-0064, #590), read once for a switch: the face's
  // words, the Ports tab's table, its port detail and its editor's chooser.
  const tags = useTagSpace(isSwitch && !absent ? siteId : undefined);
  const face: Face | undefined = isSwitch
    ? (ports.data ? portsOf(ports.data, planned?.plan, model, liveSet, cabled, tags.space) : undefined)
    : (model && model.ports.length > 0
      ? { ports: facePorts(model), states: Object.fromEntries(model.ports.map((p) => [p.name, { intent: "unread" as const, cabled: cabled[p.name] }])) }
      : undefined);
  const hasLinks = Object.keys(cabled).length > 0;
  const plate = (
    <DeviceFaceHead device={device} name={name} face={face} photo={photoOf(model)} selection={{ ports: picked, onChange: setPicked }}
      live={isSwitch && !absent ? live : undefined} times={isSwitch && !absent ? times : undefined}
      caption={isSwitch
        ? (ports.data && <SwitchFaceCaption set={ports.data} model={model} live={liveSet} />)
        : (model && <>Drawn as <code>{model.model}</code> from the catalog, {model.ports.length} {plural(model.ports.length, "port")}, with the links declared at them. What each wired port is doing, from the access point&rsquo;s own report, and its planned settings in Design are on the Ports tab. Neither is drawn here.</>)}
      legend={isSwitch
        ? { change: !!planned, live: !!liveSet && !liveSet.unanswered, links: hasLinks }
        : { links: hasLinks, uplink: false, intent: false }} />
  );
  const head = isSwitch && (
    <>
    {plate}
    {/* Where this switch's own report and the document the cell holds
        disagree about VLANs (#574). It sits at the head rather than on the
        Ports tab because it is true of the switch on every tab, and it
        renders nothing at all when there is no disagreement — which is
        silence and not a statement that the two agree. */}
    <SwitchVlanSummary d={ports.data?.vlan_disagreement} />
    </>
  );
  // The Links tab lists; the port pressed on the face at the head (#589) is
  // what it says the switch observes. A link is declared, re-worded and
  // withdrawn on Design › Wired's topology (#588, #875).
  const linksSection = (
    <DeviceLinks device={device} q={links} devices={devices} selected={selected} mayConfigure={mayConfigure} live={isSwitch ? judged : undefined} />
  );
  // One hop of the site's topology, centred on this device (#371). A switch
  // that is absent has no observation to draw and its page says so above.
  const neighbourhood = !absent && (
    <Suspense fallback={<Loading label="Loading the neighborhood" />}>
      <Neighbourhood siteId={siteId} device={device} links={links} devices={devices} live={isSwitch ? judged : undefined} mayLink={mayConfigure} />
    </Suspense>
  );
  // Audit is offered only to a caller who may read it at the site, the way
  // the nav hides the Audit page.
  const { scope } = useScope();
  const { me } = useSession();
  // An access point's root password, as the cell holds it and as the console
  // last met it (#1403), under the reveal's capability: the read is gated on
  // it, because a class of `default` names a public password. A generated
  // access point shows the verdict alone (ADR-0335); a source this version does
  // not understand is asked nothing.
  const showRootPassword = !isSwitch && !carried.carried.unreadable.has(device.serial) &&
    can(me, "site.password.reveal", { org_id: scope.orgId, customer_id: scope.customerId, site_id: siteId });
  // An access point's own SSID ceiling is read from the device under the
  // capability the bounded commands take (#809).
  const mayReadCeiling = can(me, "device.command", { org_id: scope.orgId, customer_id: scope.customerId, site_id: siteId });
  const tabs: Tab[] = [
    { id: "ports", label: "Ports" },
    { id: "links", label: "Links" },
    { id: "neighbourhood", label: "Neighborhood" },
    { id: "console", label: "Console" },
    ...(mayAudit ? [{ id: "audit", label: "Audit" }] : []),
    ...(mayDiagnose ? [{ id: "diagnostics", label: "Diagnostics", title: "what the controller holds for this device, live — provider staff only" }] : []),
  ];
  const tab: DeviceTab =
    (wanted === "audit" && !mayAudit) || (wanted === "diagnostics" && !mayDiagnose) ? "ports" : wanted;
  const select = (id: string) => set({ tab: id === "ports" ? undefined : id });
  // Nothing is scrolled into view any more (#370 is moot): the console is a
  // window of its own (#537), so this tab only ever holds the act and the
  // words that go with it.
  return (
    <>
      {absent && (
        <section className="panel notice" aria-label="This device is not on its controller">
          <p>
            <strong>{device.presence_reason === "never_seen" ? `${name} has not reached its controller yet.` : `${name} is not on its controller.`}</strong>{" "}
            {absentExplain(device)}
            {/* What absent is not, and the way out, behind the ⓘ beside the line (#1900). */}
            <Info label="About a device not on its controller">
              This is not the same as offline. The controller was asked and answered that it does not have this
              device — moved, deleted, or lost with a rebuilt venue; which of those is not knowable from
              here and is not guessed at. Nothing can be planned, applied, or opened for it, and the
              membership is removed by deleting it from the site on Design › Physical › Equipment
              {/* The head's Change this in Design lands there, on this device (#1801): one link, not two. */}
              {mayMaintain && <>, which <em>Change this in Design</em> above opens on this device</>}.
            </Info>
          </p>
        </section>
      )}
      {head}
      {/* An access point's clients with no path (#1865): true of the device on
          every tab, so it sits at the head, and it renders nothing unless the
          newest report strands a client — silence, not a statement. */}
      {!isSwitch && !absent && <RadiusVlanNotice path={read?.radius_vlan_path} mayConsole={mayConsole} />}
      {/* An access point's thumbnail is the identity card's leading column, inside its frame (#1894):
          #1889 drew it beside the card, in a column of its own outside it. */}
      <section className="panel identity" aria-label="Identity">
        {!isSwitch && face && face.ports.length > 0 && <DeviceThumb name={name} photo={photoOf(model)}>{plate}</DeviceThumb>}
        <div className="facts">
        {/* The state is the head's (#329): online, offline or absent, in the
            words `statusOf` gives, once on the page rather than twice. */}
        {/* The identifiers copy whole from where they are read (#2344), and each opens the
            device's one menu, named for it (#2367); the site has its menu. */}
        <Fact k="Serial"><IdRef entity={entityOf(device)} value={device.serial} copy="Serial" always /></Fact>
        <Fact k="Site" hint="the site the device is claimed into">{device.site_name
          ? (device.site_id ? <EntityRef entity={{ kind: "site", id: device.site_id, name: device.site_name }} always /> : device.site_name)
          : <span className="muted">—</span>}</Fact>
        {/* The family, the hardware, the catalog model and the controller are
            the line under the name (`DeviceSub`), said once (#1801). The type
            string is not on that line, so it stays here. */}
        {device.device_type && <Fact k="Type" hint="the device type string its controller carries; never what the catalog model is read from"><code>{device.device_type}</code></Fact>}
        <Fact k="MAC" hint="the device's own MAC, as the controller's record reports it">{device.mac ? <IdRef entity={entityOf(device)} value={device.mac} copy="MAC" always /> : <span className="muted">—</span>}</Fact>
        <Fact k="Firmware">{firmware && !isSwitch
          ? <FirmwareFact device={device} mayUpgrade={firmware.mayUpgrade} busy={firmware.busy} onUpgrade={firmware.onUpgrade} deliveries={firmware.deliveries} />
          : device.firmware ? <code>{device.firmware}</code> : <span className="muted">—</span>}</Fact>
        {/* ZENITH.md §11.2. */}
        <Fact k="Configuration" hint="where the device's configuration came from: generated here, or carried from another vendor and not yet reconciled"><ConfigurationCell device={device} carried={carried} /></Fact>
        <Fact k="Clients" hint="how many client devices the access point last reported, as a link to them"><ClientsCell device={device} clients={clients} /></Fact>
        {!isSwitch && !absent && <Fact k="LEDs" hint="the access point's own LED setting, or its site's, as the next plan writes it; not what the LEDs show now, which no state report carries"><ApLedsFact serial={device.serial} mayConfigure={mayConfigure} headOffers={mayMaintain} /></Fact>}
        {!isSwitch && !absent && <Fact k="RADIUS VLANs" hint="whether the access point's newest report places a client on a VLAN that client's network does not list, read with the device and never on its own"><RadiusVlanFact path={read?.radius_vlan_path} online={device.online_state === "online"} status={matchStatus} /></Fact>}
        {!isSwitch && !absent && <Fact k="SSID ceiling" hint="how many networks each of the access point's radios says it can carry, as last read from the device; read on request and never on its own"><SsidCeilingFact serial={device.serial} online={!!device.online} mayRead={mayReadCeiling} /></Fact>}
        {/* Restates ADR-0301 decision 8 (`8a`) — the two hints are re-read under the ruling: for an
            offline device the address is a past one and the hint says it may have changed since. */}
        <Fact k="Address" hint={device.online_state === "offline"
          ? "the address the device held on its own network in its last state report, before it went offline. It may have changed since. Shown from the newest report its controller still holds."
          : "the address the device holds on its own network, from its last state report: the one to reach it at"}>
          <AddressWords device={device} read={read} status={matchStatus} />
        </Fact>
        <Fact k="Seen from" hint={device.online_state === "offline"
          ? "where its connection last came from while it was connected; not a way to reach the device"
          : "where the connection appeared to come from when it reached the controller; not a way to reach the device"}>
          <SeenFromWords device={device} read={read} />
        </Fact>
        <Fact k="Last contact"><When value={device.last_contact_at} /></Fact>
        <Fact k="Uptime">{up ?? <span className="muted">—</span>}</Fact>
        {device.description && <Fact k="Description">{device.description}</Fact>}
        </div>
      </section>
      {/* The model's product sheet (#2026, ADR-0478 decision 7a): the same groups the Device
          models page draws, from the `/device-models/{model}` read this page already makes —
          no second read, and no copy of the facts on the device. Folded, because on a device's
          page it is reference and not state. */}
      {model && hasSheet(model) && (
        <details className="panel model-sheet-panel">
          <summary>Hardware <span className="dim">· <code>{model.model}</code></span></summary>
          <ModelSheet model={model} />
        </details>
      )}
      {/* Under the facts and above the tabs, so it is the same on every tab, and outside the
          head's acts, which are §11.2's (#1864; §18 is silent on its place). */}
      {mayAskAssistant && <DeviceDiagnosis key={device.serial} serial={device.serial} />}
      <Tabs tabs={tabs} current={tab} onSelect={select} label={`${name}'s sections`} />
      {tab === "ports" && (
        <TabPanel id="ports">
          {isSwitch && ports.data && face
            ? (
              <>
                {/* The way to the ports in Design is a link in words and not a
                    second *Change this in Design*: the head's is the page's one
                    (#1801), and it lands on the device's record. */}
                <SwitchPorts device={device} read={read} readStatus={matchStatus} set={ports.data} face={face} tags={tags} reload={ports.reload} live={live} times={times}
              picked={picked} onPick={setPicked} plan={planned?.plan} planReadAt={planned?.readAt} onPlan={onPlan} mayReadPoE={mayReadRunning}
              onward={mayConfigure && <ScopeLink to="/design/wired/ports" facets={{ serial: device.serial, ...(selected ? { port: selected } : {}) }}
                title={selected ? `Describe, plan and apply ${selected} on Design › Wired's Ports tab.` : `Describe, plan and apply ${name}'s ports on Design › Wired's Ports tab.`}>Plan these ports in Design</ScopeLink>} />
              </>
            )
            : !isSwitch
              // An access point's wired ports, read from its own report beside the
              // intent Design holds, through the one row component Operate › Wired's
              // table renders them with (#747, #1353, ADR-0245 decision 5c).
              ? <AccessPointPortsPanel device={device} mayConfigure={mayConfigure} />
              : (
              <Card className="panel" title="Ports">
                {absent && (
                  <p className="muted">The controller does not have this device, so there is no uplink to read and no planned settings to apply. The ports are shown again if it comes back; otherwise the switch is deleted from the site on Design › Physical › Equipment.</p>
                )}
                {!absent && ports.status === "loading" && <Loading label="Loading the planned port settings" />}
                {!absent && ports.status === "error" && <ProblemBox problem={ports.problem} />}
              </Card>
            )}
          {/* The held and running pair, a switch's console read and the
              provider's part of what the controller holds, as one section
              (#1801). A plan's port comparison is Design › Wired's. */}
          <Configuration device={device} read={read} readStatus={matchStatus} convergence={convergence} mayReadRunning={mayReadRunning} mayDiagnose={mayDiagnose} />
          {isSwitch && !absent && mayReadRunning && <SwitchHealth device={device} />}
          {!isSwitch && !absent && <AccessPointReach siteId={siteId} serial={device.serial} />}
          {isSwitch && !absent && <SwitchUplinkReach siteId={siteId} serial={device.serial} />}
          {!isSwitch && <RadioOverrides serial={device.serial} />}
        </TabPanel>
      )}
      {tab === "links" && <TabPanel id="links">{linksSection}</TabPanel>}
      {tab === "neighbourhood" && (
        <TabPanel id="neighbourhood">
          {neighbourhood || (
            <Card className="panel" title="Neighborhood">
              <p className="muted">The controller does not have this device, so there is nothing observed to draw around it.</p>
            </Card>
          )}
        </TabPanel>
      )}
      {tab === "console" && (
        <TabPanel id="console">
          <div className="console-panel">
            {(
              <Card className="panel" title="Console">
                {/* The act's own words for why it cannot open (`notLiveExplain`, which DeviceActions draws too), so the tab and the head agree. */}
                {absent
                  ? <p className="muted">{absentExplain(device)}</p>
                  : !device.online
                    ? <p className="muted">{notLiveExplain(device, "a console")}</p>
                    : !mayConsole
                      /* Restates ADR-0155 decision 1a — the console is the provider's, by device class, and no role holds it. */
                      ? <p className="muted">The console is opened by platform staff.</p>
                      : (
                        <>
                          <div className="actions">
                            <button type="button" className="btn btn-primary" onClick={() => onConsole(device)}>Open the console</button>
                            {/* What the console is and what is kept of it, behind the ⓘ beside the act (#1900). */}
                            <Info label="About the console">The device&rsquo;s own shell, relayed here from its controller. Nothing typed is recorded; the audit says who opened a console and for how long.</Info>
                          </div>
                        </>
                      )}
              </Card>
            )}
            {showRootPassword && (
              <Card className="panel" title="Root password">
                <RootPasswordReadings serial={device.serial} />
              </Card>
            )}
          </div>
        </TabPanel>
      )}
      {tab === "diagnostics" && mayDiagnose && (
        <TabPanel id="diagnostics">
          <Suspense fallback={<Loading label="Loading diagnostics" />}>
            <Diagnostics serial={device.serial} />
          </Suspense>
        </TabPanel>
      )}
      {tab === "audit" && mayAudit && (
        <TabPanel id="audit">
          {/* What the list is, behind the ⓘ beside the title; the link stays in the open beside it (#1900). */}
          <Card className="panel" title="Audit"
            head={(
              <>
                <Info label="About this list">Every change recorded against this device, newest first — the Audit page&rsquo;s list, narrowed to it.</Info>
                <ScopeLink to="/audit" facets={{ serial: device.serial }}>Open in Audit</ScopeLink>
              </>
            )}>
            <AuditList filters={{ ...siteScopeQuery(scope), target_type: "device", target_id: device.serial }} deps={[device.serial, scope.orgId, scope.customerId, scope.siteId]} />
          </Card>
        </TabPanel>
      )}
    </>
  );
}

function Fact({ k, hint, children }: { k: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="fact">
      <span className="fact-k" title={hint}>{k}</span>
      <span className="fact-v">{children}</span>
    </div>
  );
}
